import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AccessDenied from "../Pages/AccessDenied";
import UpgradePlan from "../Pages/UpgradePlan";

/**
 * ProtectedRoute handles authentication AND authorization
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if authorized
 * @param {string[]} [props.roles] - Required roles for access
 * @param {string} [props.module] - Required module for access
 * @param {string} [props.feature] - Required feature for access
 * @param {boolean} [props.requireAll=false] - Require ALL roles/features instead of ANY
 * @param {string} [props.fallback="access-denied"] - "access-denied" | "upgrade-plan"
 * @param {React.ReactNode} [props.customFallback] - Custom fallback component
 */
const ProtectedRoute = ({
  children,
  roles = [],
  module,
  feature,
  requireAll = false,
  fallback = "access-denied",
  customFallback,
}) => {
  const { isAuthenticated, isLoading, isAuthorized } = useAuth();

  // Wait until AuthContext finishes loading
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // 🚨 Not logged in → send to login page
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Now do fine-grained authorization check
  const authConfig = { roles, module, feature, requireAll };
  const hasAccess = isAuthorized(authConfig);

  if (!hasAccess) {
    if (customFallback) return customFallback;
    if (fallback === "upgrade-plan") return <UpgradePlan />;
    return <AccessDenied />;
  }

  // ✅ All good → render children
  return children;
};

export default ProtectedRoute;
